import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { PushService } from '@/services/push/push.service';
import {
  checkNotificationPermission,
  ensurePushChannels,
  getPushChannelStates,
  onPermissionChange,
  registerPushCategories
} from '@/services/push/notificationPermission';
import {
  UUID_V4_RE,
  buildDevicePayload,
  extractNotificationData,
  tokenRetryDelay,
  uuidV4FromBytes
} from '@/services/push/pushTarget';
import { listenForegroundNotificationTaps, showForegroundNotification } from '@/services/push/foregroundNotification';
import { handleAppLink, handlePushTap, reportPushEvent, setupPushRouter } from '@/services/push/pushRouter';

// Push remoto do app do aluno (STORY-1642, fatia 0): listeners nativos,
// registro do aparelho no EadApi e reação a login/logout/resume.
//
// Build SEM Firebase (sem google-services.json / GoogleService-Info.plist) é um
// caminho suportado, não um erro: o app abre normal e o push fica inativo.
//   - iOS: o plugin (8.5.x) não chama `FirebaseApp.configure()` sem o plist e
//     não toca em `Messaging.messaging()` no load; `getToken` rejeita com
//     `UNAVAILABLE`. O AppDelegate também só configura se o plist existir.
//   - Android: o load do plugin não usa Firebase; `getToken` rejeita com
//     "Default FirebaseApp is not initialized" (o plugin pega a exceção).
// Nos dois, a 1ª rejeição dessas marca `push/available = false` e mais nada
// chama o plugin do Firebase nesta sessão.

const INSTALLATION_KEY = 'ead_push_installation_id';
const BOOT_COUNT_KEY = 'ead_push_boot_count';
// Logout que não conseguiu desligar o aparelho (sem rede, timeout, sessão já
// morta) nem apagar o token no FCM. Sem a marca, a linha seguiria ativa e o SO
// continuaria mostrando, na tela deste aparelho, os pushes do aluno que saiu.
// Ela sobrevive ao fechamento do app e é resolvida no próximo boot, resume ou
// reconexão.
const PENDING_UNREGISTER_KEY = 'ead_push_pending_unregister';
// O registro sai a cada resume, mas sem repetir o MESMO corpo em menos de 30 min:
// o que o backend precisa do resume é o `DataUltimoRegistro` (janela de 30 dias
// para desativar aparelho parado) e qualquer MUDANÇA (permissão, canal, token),
// que sempre vai na hora porque muda o corpo.
const RESYNC_MS = 30 * 60 * 1000;
const SYNC_DEBOUNCE_MS = 400;

let _store = null;
let _initialized = false;
let _token = null;
let _lastSent = null;
let _lastStudentId = null;
let _pendingSync = false;
let _syncTimer = null;
let _syncRunning = null;
let _syncAgain = false;
let _syncForce = false;
let _tokenRetryTimer = null;
let _tokenRetryCount = 0;
let _flushingUnregister = null;

const isNative = () => Capacitor.isNativePlatform();
const platform = () => Capacitor.getPlatform();

function safeGet (key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}

function safeSet (key, value) {
  try { localStorage.setItem(key, value); } catch (e) {}
}

function safeRemove (key) {
  try { localStorage.removeItem(key); } catch (e) {}
}

function generateUuidV4 () {
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (c && typeof c.randomUUID === 'function') {
    try { return c.randomUUID(); } catch (e) {}
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return uuidV4FromBytes(bytes);
}

// UUID v4 gerado UMA vez por instalação. É a chave do aparelho no backend
// (`CodInstalacao`): o mesmo aparelho com outro aluno logado troca de dono, não
// duplica linha.
export function getInstallationId ({ create = true } = {}) {
  const stored = safeGet(INSTALLATION_KEY);
  if (stored && UUID_V4_RE.test(stored)) return stored;
  if (!create) return null;
  const id = generateUuidV4();
  safeSet(INSTALLATION_KEY, id);
  return id;
}

function bumpBootCount () {
  const n = parseInt(safeGet(BOOT_COUNT_KEY), 10);
  const next = (Number.isFinite(n) && n > 0 ? n : 0) + 1;
  safeSet(BOOT_COUNT_KEY, String(next));
  return next;
}

export function getBootCount () {
  const n = parseInt(safeGet(BOOT_COUNT_KEY), 10);
  return Number.isFinite(n) ? n : 0;
}

function currentStudentId () {
  const user = _store && _store.state.user.currentUser;
  return (user && user.studentId) || null;
}

function setAvailable (value) {
  if (_store && _store.state.push.available !== value) _store.commit('push/SET_AVAILABLE', value);
}

function isAvailable () {
  return _store ? _store.state.push.available : null;
}

function isNotConfiguredError (e) {
  const code = e && e.code;
  const message = String((e && (e.message || e.errorMessage)) || e || '');
  return code === 'UNAVAILABLE' ||
    code === 'UNIMPLEMENTED' ||
    /not configured|FirebaseApp is not initialized|Default FirebaseApp|GoogleService-Info|google_app_id/i.test(message);
}

// Backoff com teto (pushTarget.tokenRetryDelay): esgotado, só o próximo resume
// ou um `tokenReceived` volta a tentar.
function scheduleTokenRetry () {
  if (_tokenRetryTimer) return;
  const delay = tokenRetryDelay(_tokenRetryCount);
  if (delay == null) return;
  _tokenRetryCount++;
  _tokenRetryTimer = setTimeout(() => {
    _tokenRetryTimer = null;
    requestPushSync('token-retry');
  }, delay);
}

function resetTokenRetry () {
  _tokenRetryCount = 0;
}

async function getFcmToken () {
  if (isAvailable() === false) return null;
  if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
    setAvailable(false);
    return null;
  }
  try {
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return null;
    _token = token;
    resetTokenRetry();
    setAvailable(true);
    return token;
  } catch (e) {
    if (isNotConfiguredError(e)) {
      setAvailable(false);
      console.info('[push] Firebase não configurado neste build: push remoto inativo.');
    } else {
      // iOS antes do token APNs chegar ("No APNS token specified...") ou rede.
      // O `tokenReceived` costuma resolver sozinho; a retentativa cobre o resto.
      console.warn('[push] getToken falhou:', e?.message || e);
      scheduleTokenRetry();
    }
    return null;
  }
}

// Garante que sabemos se o push funciona neste build (para a UI decidir se
// oferece o pedido de permissão). Com aluno logado, tenta obter o token.
export async function ensurePushAvailability () {
  if (!isNative()) return false;
  if (isAvailable() !== null) return isAvailable();
  if (!currentStudentId()) return null;
  await getFcmToken();
  return isAvailable();
}

async function appVersion () {
  try {
    const info = await App.getInfo();
    return info && info.version;
  } catch (e) {
    return null;
  }
}

function timeZone () {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}

export async function refreshPushPermissionState () {
  if (!isNative() || !_store) return;
  const [permission, channels] = await Promise.all([checkNotificationPermission(), getPushChannelStates()]);
  _store.commit('push/SET_PERMISSION', permission);
  _store.commit('push/SET_CHANNELS', channels);
  return { permission, channels };
}

async function syncDevice (force) {
  const sid = currentStudentId();
  if (!sid) return;
  if (!_store.state.dom.isConnected) {
    _pendingSync = true;
    return;
  }
  const token = await getFcmToken();
  if (!token) return;

  const state = await refreshPushPermissionState();
  const body = buildDevicePayload({
    token,
    platform: platform(),
    appVersion: await appVersion(),
    timeZone: timeZone(),
    permission: state && state.permission,
    channels: state && state.channels
  });

  // Dono entra na chave: outro aluno no mesmo aparelho sempre reenvia.
  const key = `${sid}|${JSON.stringify(body)}`;
  if (!force && _lastSent && _lastSent.key === key && Date.now() - _lastSent.at < RESYNC_MS) return;

  try {
    await PushService.registerDevice(getInstallationId(), body);
    // Logout/troca de conta durante o PUT: o registro vale para quem estava
    // logado quando saiu, e o próximo login reenvia.
    if (currentStudentId() === sid) _lastSent = { key, at: Date.now() };
    _pendingSync = false;
    // A linha desta instalação agora é de quem está logado, com o token atual:
    // um desregistro pendente de quem saiu antes não tem mais o que desligar.
    clearPendingUnregister();
  } catch (e) {
    _pendingSync = true;
    console.warn('[push] registro do aparelho falhou:', e?.message || e);
  }
}

// Coalesce: resume + mudança de permissão + token novo chegam juntos.
export function requestPushSync (reason, { force = false } = {}) {
  if (!_store || !isNative()) return;
  if (force) _syncForce = true;
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(runSync, SYNC_DEBOUNCE_MS);
}

async function runSync () {
  _syncTimer = null;
  if (_syncRunning) {
    _syncAgain = true;
    return;
  }
  const force = _syncForce;
  _syncForce = false;
  _syncRunning = syncDevice(force).catch(() => {});
  try {
    await _syncRunning;
  } finally {
    _syncRunning = null;
    if (_syncAgain) {
      _syncAgain = false;
      requestPushSync('again');
    }
  }
}

function loadPreferencesQuietly () {
  if (!_store || !currentStudentId()) return;
  _store.dispatch('push/loadPreferences', { force: true }).catch(() => {});
}

// Carga das preferências que não aconteceu (login sem rede) ou falhou: tenta de
// novo quando a rede volta e quando o app volta do background. Sem isso o item
// "Silenciar avisos deste curso" do menu sumia até o próximo login.
function retryMissingPreferences () {
  if (!_store || !_store.state.dom.isConnected) return;
  if (_store.state.push.preferencesLoaded) return;
  loadPreferencesQuietly();
}

function onUserChanged (studentId) {
  if (studentId && studentId !== _lastStudentId) {
    _lastStudentId = studentId;
    _lastSent = null;
    _store.commit('push/RESET_PREFERENCES');
    requestPushSync('login', { force: true });
    if (_store.state.dom.isConnected) loadPreferencesQuietly();
  } else if (!studentId && _lastStudentId) {
    _lastStudentId = null;
    _lastSent = null;
    _store.commit('push/RESET_PREFERENCES');
  }
}

function onTokenReceived (token) {
  if (!token) return;
  resetTokenRetry();
  setAvailable(true);
  if (token === _token) return;
  _token = token;
  requestPushSync('token');
}

function onResume () {
  refreshPushPermissionState().catch(() => {});
  resetTokenRetry();
  requestPushSync('resume');
  flushPendingUnregister().catch(() => {});
  retryMissingPreferences();
  if (currentStudentId() && _store.state.dom.isConnected) {
    // Sino fresco ao voltar: o polling pula os ticks com o app em background.
    _store.dispatch('notification/getNotifications', { silent: true }).catch(() => {});
  }
}

// Push recebido com o app ABERTO. O iOS não mostra banner (presentationOptions
// []) e o Android nunca mostra em primeiro plano — o aviso vira notificação
// local do sistema (services/push/foregroundNotification), que abre a aula ao
// toque, e o sino é recarregado.
function onForegroundPush (event) {
  const data = extractNotificationData(event);
  reportPushEvent('received_fg', data);
  if (currentStudentId() && _store.state.dom.isConnected) {
    _store.dispatch('notification/getNotifications', { silent: true }).catch(() => {});
  }

  // O toque na notificação local é uma abertura como a do push; `opened` é ecoado lá.
  showForegroundNotification(event && event.notification, data).catch(() => {});
}

function markPendingUnregister () {
  safeSet(PENDING_UNREGISTER_KEY, String(Date.now()));
}

function clearPendingUnregister () {
  safeRemove(PENDING_UNREGISTER_KEY);
}

export function hasPendingUnregister () {
  return Boolean(safeGet(PENDING_UNREGISTER_KEY));
}

// Apaga o token no FCM: o próximo envio para ele volta UNREGISTERED e o
// MobileNotification desativa a linha (motivo 2). `true` = nada mais a fazer
// (apagou, ou não há Firebase neste build e portanto não há token).
async function deleteFcmToken () {
  if (isAvailable() === false || !Capacitor.isPluginAvailable('FirebaseMessaging')) return true;
  try {
    await FirebaseMessaging.deleteToken();
    _token = null;
    return true;
  } catch (e) {
    if (isNotConfiguredError(e)) {
      setAvailable(false);
      return true;
    }
    return false;
  }
}

// Resolve um desregistro que o logout não conseguiu fazer. Só sem aluno logado:
// com alguém logado, o próximo registro (PUT) já leva a linha para ele — e
// apagar o token agora derrubaria o push de quem está usando o app.
export function flushPendingUnregister () {
  if (!isNative() || !hasPendingUnregister() || currentStudentId()) return Promise.resolve(false);
  if (_store && !_store.state.dom.isConnected) return Promise.resolve(false);
  if (_flushingUnregister) return _flushingUnregister;
  _flushingUnregister = deleteFcmToken()
    .then(done => {
      if (done) clearPendingUnregister();
      return done;
    })
    .catch(() => false)
    .finally(() => { _flushingUnregister = null; });
  return _flushingUnregister;
}

// Limpa a bandeja no logout: as notificações de quem saiu ficariam lá, e quem
// logar depois no mesmo aparelho tocaria nelas (com a sessão dele). Remove
// também as notificações locais (ex.: "Download concluído"), o que é aceitável
// no logout. Nunca lança.
function clearDeliveredNotifications () {
  if (isAvailable() === false || !Capacitor.isPluginAvailable('FirebaseMessaging')) return;
  try {
    Promise.resolve(FirebaseMessaging.removeAllDeliveredNotifications()).catch(() => {});
  } catch (e) {}
}

// Chamado pelo AuthService no logout, ANTES do `_resetAuthData` (o token de
// acesso é capturado por quem chama). Nunca lança e nunca segura o logout além
// do timeout curto do DELETE.
export async function unregisterPushDevice (accessToken) {
  if (!isNative()) return;
  clearDeliveredNotifications();
  _lastSent = null;
  const installationId = getInstallationId({ create: false });
  if (!installationId) return;
  let ok = false;
  if (accessToken) {
    try {
      await PushService.unregisterDevice(installationId, accessToken);
      ok = true;
    } catch (e) {}
  }
  if (ok) {
    clearPendingUnregister();
    return;
  }
  // Sem confirmação do backend o token continuaria ativo para o aluno que saiu.
  // A marca vem ANTES da tentativa: sem rede o deleteToken também falha, e aí é
  // o próximo boot/resume/reconexão que resolve (flushPendingUnregister).
  markPendingUnregister();
  deleteFcmToken()
    .then(done => { if (done) clearPendingUnregister(); })
    .catch(() => {});
}

export function getPushStore () {
  return _store;
}

// Chamado no main.js ANTES do `new Vue` e antes dos awaits do boot: os listeners
// de toque/link precisam existir cedo. (Os eventos do plugin são retidos até
// alguém escutar, mas registrar cedo evita depender disso.)
export function initPush ({ store, router }) {
  if (_initialized) return;
  _store = store;
  setupPushRouter({ store, router });
  if (!isNative()) {
    store.commit('push/SET_AVAILABLE', false);
    store.commit('push/SET_PERMISSION', 'unsupported');
    return;
  }
  _initialized = true;
  bumpBootCount();

  if (Capacitor.isPluginAvailable('FirebaseMessaging')) {
    FirebaseMessaging.addListener('notificationActionPerformed', event => {
      handlePushTap(extractNotificationData(event));
    }).catch(() => {});
    FirebaseMessaging.addListener('notificationReceived', onForegroundPush).catch(() => {});
    listenForegroundNotificationTaps(handlePushTap);
    FirebaseMessaging.addListener('tokenReceived', ({ token }) => onTokenReceived(token)).catch(() => {});
  } else {
    setAvailable(false);
  }

  // Universal Links / App Links: o mesmo roteador do push. Antes ninguém escutava
  // e o link só abria o app na tela inicial.
  App.addListener('appUrlOpen', ({ url }) => handleAppLink(url)).catch(() => {});
  App.getLaunchUrl()
    .then(result => { if (result && result.url) handleAppLink(result.url); })
    .catch(() => {});
  App.addListener('resume', onResume).catch(() => {});

  // Setup nativo idempotente, SEM prompt de permissão.
  ensurePushChannels().catch(() => {});
  registerPushCategories().catch(() => {});
  refreshPushPermissionState().catch(() => {});

  onPermissionChange(permission => {
    store.commit('push/SET_PERMISSION', permission);
    requestPushSync('permission');
  });

  store.subscribe((mutation, state) => {
    if (mutation.type !== 'user/SET_CURRENT_USER') return;
    onUserChanged((state.user.currentUser && state.user.currentUser.studentId) || null);
  });
  store.watch(state => state.dom.isConnected, connected => {
    if (!connected) return;
    if (_pendingSync) requestPushSync('reconnect');
    flushPendingUnregister().catch(() => {});
    retryMissingPreferences();
  });

  // Aluno já hidratado antes do init (não acontece hoje, mas é barato cobrir).
  if (currentStudentId()) onUserChanged(currentStudentId());

  // Logout anterior que ficou sem desligar o aparelho (sem rede na hora). Só
  // depois do boot de auth: antes dele a sessão ainda não foi restaurada e o
  // "sem aluno logado" seria falso.
  if (store.state.auth && store.state.auth.initializing) {
    const stop = store.watch(state => state.auth.initializing, initializing => {
      if (initializing) return;
      stop();
      flushPendingUnregister().catch(() => {});
    });
  } else {
    flushPendingUnregister().catch(() => {});
  }
}
